import { useCallback, useMemo } from 'react'
import { BaseEntityTable } from './BaseEntityTable'
import { getPaymentColumns } from '@shared/lib/tables/columns/payment-columns'
import { getPaymentRowActions, getPaymentHoverActions } from '@shared/lib/actions/payment-actions'
import type { ColumnManagementConfig } from '@shared/lib/tables/utils/column-management'
import type { UserRole } from '@shared/services/session.service'
import type { Payment } from '@shared/types/payment.types'

interface PaymentTableProps {
  data: Payment[]
  selectedItems: string[]
  onSelectionChange: (selectedIds: string[]) => void
  onRowClick: (payment: Payment) => void
  pagination: {
    pageSize: number
    currentPage: number
    onPageChange: (page: number) => void
    totalItems: number
    totalPages: number
  }
  loading: boolean
  statusFilter?: string
  role?: UserRole
  onCreatePaymentLink?: () => void
  managedColumns?: ColumnManagementConfig[]
  onViewCustomer?: (payment: Payment) => void
  onSendReceipt?: (payment: Payment) => void
  onFlag?: (payment: Payment) => void
  onAddNote?: (payment: Payment) => void
  onCapture?: (payment: Payment) => void
  onRefund?: (payment: Payment) => void
  onCancel?: (payment: Payment) => void
  onRetry?: (payment: Payment) => void
  onPreview?: (payment: Payment) => void
  onApproveRefund?: (payment: Payment) => void
  onSelectSucceededOnly?: (fn: () => void) => void
  activeItemId?: string
}

export function PaymentTable(props: PaymentTableProps) {
  const {
    onViewCustomer,
    onSendReceipt,
    onFlag,
    onAddNote,
    onCapture,
    onRefund,
    onCancel,
    onRetry,
    onPreview,
    onApproveRefund,
    onCreatePaymentLink,
    role,
    ...baseProps
  } = props

  const getColumnsForRole = useCallback(
    (status: string) => getPaymentColumns(status, role),
    [role],
  )

  const onCopyId = useCallback((payment: Payment) => {
    navigator.clipboard.writeText(payment.id)
  }, [])

  const onCopyChannelId = useCallback((payment: Payment) => {
    navigator.clipboard.writeText(payment.channelId || '')
  }, [])

  const rowActions = useMemo(() => getPaymentRowActions({
    onCopyId,
    onCopyChannelId,
    onViewCustomer,
    onSendReceipt,
    onFlag,
    onAddNote,
    onCapture,
    onRefund,
    onCancel,
    onPreview,
    onApproveRefund,
  }, role), [onCopyId, onCopyChannelId, onViewCustomer, onSendReceipt, onFlag, onAddNote, onCapture, onRefund, onCancel, onPreview, onApproveRefund, role])

  const hoverActions = useMemo(() => getPaymentHoverActions({
    onSendReceipt,
    onCapture,
    onRefund,
    onRetry,
  }), [onSendReceipt, onCapture, onRefund, onRetry])

  const emptyStateActions = useMemo(() =>
    onCreatePaymentLink ? [
      {
        label: "Create payment link",
        onClick: onCreatePaymentLink,
        variant: "primary" as const
      },
      {
        label: "Learn more",
        onClick: () => console.log("Learn more about payments"),
        variant: "secondary" as const
      }
    ] : undefined
  , [onCreatePaymentLink])

  return (
    <BaseEntityTable<Payment>
      {...baseProps}
      entityType="payment"
      getColumns={getColumnsForRole}
      getItemId={(payment: Payment) => payment.transactionRef}
      getItemStatus={(payment: Payment) => payment.status || 'unknown'}
      rowActions={rowActions}
      hoverActions={hoverActions}
      emptyStateActions={emptyStateActions}
    />
  )
}